import React, { useState, useEffect, useContext, useRef } from 'react';
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    Container,
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    CircularProgress,
    RadioGroup,
    Radio,
    Grid,
    Divider,
    FormLabel
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { ConfigContext, http } from '../m34n-ui/src/lib.js';
import InputMask from 'react-input-mask';

const WholeheartedLeadingForm = () => {
    const { config } = useContext(ConfigContext);
    const params = useParams();
    const [formData, setFormData] = useState({
        // Section 1: Registration Type
        registrationType: 'print',
        role: '',
        groupMembers: '',

        // Section 2: Your Information
        firstName: '',
        lastName: '',
        email: '',
        confirmEmail: '',
        phoneCountry: 'US',
        phone: '',
        birthDate: null, // Changed to null for date picker
        recipientInfo: '',

        // Section 3: Additional Information
        churchInfo: '',
        otherChurchInfo: '', // New field for 'Other' church
        hearAboutUs: [],
        otherHearAboutUs: '', // New field for 'Other' hearAboutUs
        expectations: '',
        previousAttendance: [],
        vocation: '',
        otherVocation: '', // New field for 'Other' vocation
        receiveUpdates: false,

        // Section 5: Shipping Information
        shippingAddress: '',
        shippingCity: '',
        shippingState: '',
        shippingPostalCode: '',
        shippingCountry: 'US',
        quantityOfBooks: '1', // **Changed to string**
        additionalMembers: [], // Initialize additionalMembers as an empty array

        // **New Field for Additional Participants with No Book**
        additionalParticipantsNoBook: '', // Added this line
    });

    // **New State for Quantity of Books Error**
    const [quantityError, setQuantityError] = useState('');

    const [loading, setLoading] = useState(true);
    const [headerURL, setHeaderURL] = useState('');
    const [churchOptions, setChurchOptions] = useState([]);
    const [vocationOptions, setVocationOptions] = useState([]);
    const [registrationTypes, setRegistrationTypes] = useState([]);
    const [shippingOptions, setShippingOptions] = useState([]);
    const [calculatingShipping, setCalculatingShipping] = useState(false);
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedShippingOption, setSelectedShippingOption] = useState(null);

    useEffect(() => {
        async function fetchProductDetails() {
            const product = await http.get({}, `${config.uri.base}/catalog/product/${params.productId}`);
            if (product.headerURL) {
                setHeaderURL(product.headerURL);
            }
            if (product.registrationTypes) {
                setRegistrationTypes(product.registrationTypes);
            }
        }

        async function fetchChurchOptions() {
            const options = await http.get({}, `${config.uri.base}/forms/options/Church`);
            setChurchOptions(options);
        }

        async function fetchStateOptions() {
            const options = await http.get({}, `${config.uri.base}/forms/options/StateList`);
            setStateOptions(options);
        }

        async function fetchVocationOptions() {
            const options = await http.get({}, `${config.uri.base}/forms/options/Vocation`);
            setVocationOptions(options);
        }

        async function fetchData() {
            await fetchProductDetails();
            await fetchChurchOptions();
            await fetchVocationOptions();
            await fetchStateOptions();
            setLoading(false);
        }

        fetchData();
    }, [config.uri.base, params.productId]);

    const handleChange = (e) => {
        let name, value;
        if (e.target) {
            name = e.target.name;
            value = e.target.value;
        } else {
            name = e.name;
            value = e.value;
        }
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData({
            ...formData,
            [name]: checked,
        });
    };

    const handleMultiCheckboxChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData((prevState) => {
            const values = prevState[name];
            if (checked) {
                return {
                    ...prevState,
                    [name]: [...values, value],
                };
            } else {
                return {
                    ...prevState,
                    [name]: values.filter((v) => v !== value),
                };
            }
        });
    };

    const formatPostalCode = (code) => {
        // Format postal code (you can add more country-specific formatting here)
        if (formData.shippingCountry === 'US') {
            return code.replace(/[^\d]/g, '').substr(0, 5);
        }
        return code;
    };

    const validateShippingFields = () => {
        const requiredFields = ['shippingAddress', 'shippingCity', 'shippingState', 'shippingPostalCode', 'shippingCountry'];
        return requiredFields.every(field => formData[field].trim() !== '');
    };

    const handleCalculateShipping = async () => {
        if (!validateShippingFields()) {
            alert('Please fill in all required shipping fields.');
            return;
        }

        setCalculatingShipping(true);
        try {
            const parsedQuantity = parseInt(formData.quantityOfBooks, 10);
            const quantity = isNaN(parsedQuantity) ? 1 : parsedQuantity;

            const response = await http.post({
                address: formData.shippingAddress,
                city: formData.shippingCity,
                state: formData.shippingState,
                postalCode: formData.shippingPostalCode,
                country: formData.shippingCountry,
                productId: params.productId,
                registrationType: formData.registrationType,
                quantityOfBooks: quantity, // **Ensure it's a number**
            }, `${config.uri.base}/shipping-options`);
            setShippingOptions(response);
            setSelectedShippingOption(null);
        } catch (error) {
            console.error('Error calculating shipping options:', error);
            alert('Error calculating shipping options. Please try again.');
        } finally {
            setCalculatingShipping(false);
        }
    };

    const handleShippingOptionSelect = (option) => {
        setSelectedShippingOption(option);
    };

    const [emailError, setEmailError] = useState('');
    const confirmEmailRef = useRef(null);

    const validateEmails = () => {
        if (formData.email !== formData.confirmEmail) {
            setEmailError('Emails do not match');
            confirmEmailRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        } else {
            setEmailError('');
            return true;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Ensure a shipping option has been selected
        if (!selectedShippingOption) {
            alert('Please select a shipping option before submitting.');
            return;
        }

        if (!validateEmails()) {
            return;
        }

        // **Validate Quantity of Books**
        const qty = parseInt(formData.quantityOfBooks, 10);
        if (isNaN(qty) || qty < 1) {
            setQuantityError('Please enter a valid number of books (at least 1).');
            // Scroll to the Quantity of Books field
            const qtyField = document.getElementById('quantityOfBooks');
            if (qtyField) {
                qtyField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        } else {
            setQuantityError('');
        }

        const orderResult = await http.post({
            ...formData,
            quantityOfBooks: qty, // **Convert to number**
            selectedShippingOption,
            productId: params.productId,
        }, `${config.uri.base}/print-jobs`);
        window.location.href = orderResult.paymentLink;
    };

    const handleQtyChange = (e) => {
        const { value } = e.target;
        // Allow empty string or digits only
        if (value === '' || /^\d+$/.test(value)) {
            setFormData((prevData) => ({
                ...prevData,
                quantityOfBooks: value,
            }));
            // Reset quantity error on change
            if (quantityError) {
                setQuantityError('');
            }
        }
    };

    // Update additionalMembers array when quantityOfBooks changes
    useEffect(() => {
        const qty = parseInt(formData.quantityOfBooks, 10);
        if (isNaN(qty) || qty < 1) {
            // Invalid quantity; do not update additionalMembers
            setFormData(prevData => ({
                ...prevData,
                additionalMembers: [],
            }));
            return;
        }

        setFormData(prevData => {
            const { quantityOfBooks, additionalMembers } = prevData;
            const newQty = parseInt(quantityOfBooks, 10);
            const newAdditionalMembers = [...additionalMembers];

            if (newQty - 1 > additionalMembers.length) {
                // Add empty objects to the array
                for (let i = additionalMembers.length; i < newQty - 1; i++) {
                    newAdditionalMembers.push({});
                }
            } else if (newQty - 1 < additionalMembers.length) {
                // Remove extra entries from the array
                newAdditionalMembers.length = newQty - 1;
            }
            return {
                ...prevData,
                additionalMembers: newAdditionalMembers
            };
        });

        // Reset shipping options and selected option when quantity changes
        setShippingOptions([]);
        setSelectedShippingOption(null);
    }, [formData.quantityOfBooks]);

    // Handle change for additional members
    const handleAdditionalMemberChange = (index, e) => {
        const { name, value } = e.target;
        const updatedMembers = [...formData.additionalMembers];
        updatedMembers[index] = { ...(updatedMembers[index] || {}), [name]: value };
        setFormData((prevData) => ({
            ...prevData,
            additionalMembers: updatedMembers
        }));
    };

    // Render additional members based on the number of books
    const renderAdditionalMembers = () => {
        const additionalInputs = [];
        const qty = parseInt(formData.quantityOfBooks, 10) || 0; // **Define qty here**
        for (let i = 0; i < qty - 1; i++) {
            additionalInputs.push(
                <Grid container spacing={2} key={i}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="First Name"
                            name="firstName"
                            value={formData.additionalMembers[i]?.firstName || ''}
                            onChange={(e) => handleAdditionalMemberChange(i, e)}
                            fullWidth
                            margin="normal"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Last Name"
                            name="lastName"
                            value={formData.additionalMembers[i]?.lastName || ''}
                            onChange={(e) => handleAdditionalMemberChange(i, e)}
                            fullWidth
                            margin="normal"
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Email"
                            name="email"
                            type='email'
                            value={formData.additionalMembers[i]?.email || ''}
                            onChange={(e) => handleAdditionalMemberChange(i, e)}
                            fullWidth
                            margin="normal"
                            required
                        />
                    </Grid>
                </Grid>
            );
        }
        return additionalInputs;
    };

    return (
        <Container>
            {loading ? (
                <Box textAlign="center" my={4}>
                    <CircularProgress />
                </Box>
            ) : (
                    <Box my={4} textAlign="center">
                        {headerURL ? (
                            <img src={headerURL} alt="Wholehearted Leading" style={{ maxWidth: '100%', height: 'auto' }} />
                        ) : (
                                <Typography variant="h6">Header image not available</Typography>
                            )}
                    </Box>
                )}
            <form onSubmit={handleSubmit}>
                {/* Section 1: Registration */}
                <Box mb={4}>
                    <Typography variant="h5">Register</Typography>
                    <FormControl component="fieldset">
                        <RadioGroup
                            name="registrationType"
                            value={formData.registrationType}
                            onChange={handleChange}
                            row
                        >
                            {registrationTypes.map((type) => (
                                <FormControlLabel
                                    key={type.type}
                                    value={type.type}
                                    control={<Radio />}
                                    label={type.name}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>
                {/* Add the new Quantity of Books field */}
                <Box mb={4}>
                    <Typography variant="h6" align="center">Quantity of Books</Typography>
                    <TextField
                        id="quantityOfBooks" // Added ID for scrolling
                        label="Quantity of Books"
                        name="quantityOfBooks"
                        type="text" // Changed from "number" to "text"
                        value={formData.quantityOfBooks}
                        onChange={handleQtyChange}
                        fullWidth
                        margin="normal"
                        required
                        // Removed inputProps min to allow empty input
                        onWheel={(e) => e.target.blur()}
                        error={!!quantityError}
                        helperText={quantityError}
                        placeholder="Enter the number of books"
                    />
                </Box>
                <Box mb={4}>
                    <Typography variant="h5" align="center">YOUR INFORMATION</Typography>
                    <Typography variant="h6" align="center">PERSONAL</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Confirm Email"
                                name="confirmEmail"
                                type="email"
                                ref={confirmEmailRef}
                                error={!!emailError}
                                helperText={emailError}
                                value={formData.confirmEmail}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        {/* Phone Number */}
                        <Grid item xs={12} md={6}>
                            <InputMask
                                mask="(999) 999-9999"
                                value={formData.phone}
                                onChange={handleChange}
                            >
                                {(inputProps) => (
                                    <TextField
                                        {...inputProps}
                                        label="Phone Number"
                                        name="phone"
                                        fullWidth
                                        margin="normal"
                                        required
                                    />
                                )}
                            </InputMask>
                        </Grid>
                    </Grid>
                </Box>
                {/* Render additional members if quantity > 1 */}
                {parseInt(formData.quantityOfBooks, 10) > 1 && (
                    <Box mb={4}>
                        <Typography variant="h6" align="center">Additional Individuals</Typography>
                        <Typography variant="body2" align="center">Please include the names and email addresses of those who will be going through this book.</Typography>
                        {renderAdditionalMembers()}
                    </Box>
                )}
                {/* **New Section: Additional Participants with No Book** */}
                <Box mb={4}>
                    <Typography variant="h6" align="center">Additional Participants with No Book</Typography>
                    <TextField
                        label="Additional Participants with No Book"
                        name="additionalParticipantsNoBook"
                        value={formData.additionalParticipantsNoBook}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                        placeholder="Enter the names and any relevant information for participants who are not receiving a book but are participating in your small group."
                    />
                </Box>
                <Box mb={4}>
                    <FormGroup>
                        <Typography>How did you hear about this small group experience?</Typography>
                        {[
                            'A friend',
                            'My church',
                            'Newsletter/email',
                            'PCA General Assembly',
                            'Social media',
                            'Wellspring Group website',
                            'Other'
                        ].map((option) => (
                            <FormControlLabel
                                key={option}
                                control={
                                    <Checkbox
                                        name="hearAboutUs"
                                        value={option}
                                        checked={formData.hearAboutUs.includes(option)}
                                        onChange={handleMultiCheckboxChange}
                                    />
                                }
                                label={option}
                            />
                        ))}
                        {formData.hearAboutUs.includes('Other') && (
                            <TextField
                                label="Please specify"
                                name="otherHearAboutUs"
                                value={formData.otherHearAboutUs}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        )}
                    </FormGroup>
                    <Divider style={{ marginTop: '20px', marginBottom: '20px' }} />

                    <div>
                        <Checkbox
                            name="receiveUpdates"
                            checked={formData.receiveUpdates}
                            onChange={handleCheckboxChange}
                            required
                        />
                        <label>
                            <span>
                                I understand that by registering I will be sent information from time to time regarding Wellspring Group and other offerings and that I can opt out at any time. Please note that all sales are final. We do not accept returns at this time. If you have a question about an item, please contact <a style={{ textDecoration: 'underline', color: 'blue' }} href="mailto:heatherlyb@wellspringgroup.org">heatherlyb@wellspringgroup.org</a>.
                            We are happy to answer any questions about a product prior to purchasing. Purchases that are declined by Stripe (credit card processing services) are subject to a service fee.
                            </span>
                        </label>
                    </div>

                </Box>
                {/* Shipping Information Section */}
                <Box mb={4}>
                    <Typography variant="h5" align="center">SHIPPING INFORMATION</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Shipping Address"
                                name="shippingAddress"
                                value={formData.shippingAddress}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Shipping City"
                                name="shippingCity"
                                value={formData.shippingCity}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth margin="normal" required>
                                <InputLabel id="shipping-state-label">Shipping State</InputLabel>
                                <Select
                                    labelId="shipping-state-label"
                                    id="shippingState"
                                    name="shippingState"
                                    value={formData.shippingState}
                                    onChange={handleChange}
                                    label="Shipping State"
                                >
                                    {stateOptions.map((state) => (
                                        <MenuItem key={state.abbreviation} value={state.abbreviation}>
                                            {state.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Shipping Postal Code"
                                name="shippingPostalCode"
                                value={formData.shippingPostalCode}
                                onChange={(e) => {
                                    const formatted = formatPostalCode(e.target.value);
                                    setFormData({ ...formData, shippingPostalCode: formatted });
                                }}
                                fullWidth
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Shipping Country</InputLabel>
                                <Select
                                    name="shippingCountry"
                                    value={formData.shippingCountry}
                                    onChange={handleChange}
                                    required
                                >
                                    <MenuItem value="US">United States</MenuItem>
                                    <MenuItem value="CA">Canada</MenuItem>
                                    {/* Add other countries as needed */}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Box textAlign="center" my={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCalculateShipping}
                            disabled={calculatingShipping || !validateShippingFields()}
                        >
                            {calculatingShipping ? 'Calculating...' : 'Calculate Shipping Options'}
                        </Button>
                    </Box>
                    {shippingOptions.length > 0 && (
                        <Box my={4}>
                            <Typography variant="h6">Available Shipping Options:</Typography>
                            <Typography variant="body2">
                                Fulfillment is controlled by a 3rd party and shipments may be delayed within the time windows provided by the 3rd party due to availability.
    </Typography>
                            <RadioGroup
                                value={selectedShippingOption ? String(selectedShippingOption.id) : ''}
                                onChange={(e) => {
                                    const selectedOption = shippingOptions.find(opt => String(opt.id) === e.target.value);
                                    console.log('Selected Option:', selectedOption, e.target.value, shippingOptions);
                                    handleShippingOptionSelect(selectedOption);
                                }}
                            >
                                {shippingOptions.map((option) => (
                                    <FormControlLabel
                                        key={option.id}
                                        value={String(option.id)}
                                        control={<Radio required />}
                                        label={
                                            <Box>
                                                <Typography>{`${option.carrier_service_name} - $${option.cost_excl_tax.toFixed(2)}`}</Typography>
                                                <Typography variant="body2">{`Estimated Print Completion Date: ${option.min_dispatch_date} through ${option.max_dispatch_date}`}</Typography>
                                                <Typography variant="body2">{`Estimated Delivery Date: ${option.min_delivery_date} through ${option.max_delivery_date}`}</Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        </Box>

                    )}
                </Box>
                <Box
                    textAlign="center"
                    mb={4}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!(shippingOptions.length > 0 && selectedShippingOption)}
                    >Submit</Button>
                </Box>
            </form>
        </Container>
    );
};

export default WholeheartedLeadingForm;

